import type { FulfillmentOrderDetail, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';

/** Mirrors the Facade's visible eligibility gates; the transaction remains authoritative. */
export const offersLineToExistingInventory = (
  line: FulfillmentOrderLine,
  detail: Pick<FulfillmentOrderDetail, 'header' | 'tracking' | 'reservations'>,
): boolean => {
  const holds = detail.reservations.filter((hold) => hold.lineNo === line.lineNo && hold.state === 'ACTIVE');
  return (
    line.sourcingRoute === 'JIT' &&
    ['ORDERED', 'RESERVED', 'AWAITING_INBOUND'].includes(line.lineStatus) &&
    ['ORDERED', 'AWAITING_INBOUND', 'READY'].includes(detail.header.fulfillmentStatus) &&
    Boolean(line.updatedAt) &&
    !line.purchaseOrderNumber?.trim() &&
    !line.lineTrackingNumber?.trim() &&
    !detail.tracking.some((row) => row.kind === 'INBOUND' && row.refId === `${detail.header.orderId}#${line.lineNo}`) &&
    line.totalQuantity > 0 &&
    holds.every((hold) => hold.kind === 'JIT_COMMITTED') &&
    holds.reduce((sum, hold) => sum + hold.qty, 0) === line.totalQuantity
  );
};

const REFUSALS: Record<string, string> = {
  INSUFFICIENT_FREE_STOCK:
    'There is not enough free stock for the whole line at this location. Refresh locations and choose another shelf, or keep awaiting purchase. Stock held by other orders also counts.',
  LINE_NOT_SWITCHABLE:
    'This line can no longer use existing stock. It may already be received, cancelled, shipped, or no longer be a JIT line, or the order may be closed. Close this dialog and check the order.',
  LINE_HAS_PURCHASE_ORDER:
    'This line already has a purchase order number. Check the purchase with the sourcing team before using existing stock; this action does not cancel a purchase.',
  LINE_HAS_INBOUND_TRACKING:
    'This line already has inbound tracking. Check the incoming parcel before using existing stock; this action does not cancel a delivery.',
  LINE_RESERVATION_NOT_JIT:
    'The whole line is no longer reserved for a JIT purchase. Close this dialog and check the order reservations.',
  CONFLICT:
    'This line changed since the dialog opened. Close it and reopen from the refreshed order before trying again.',
  IDEMPOTENCY_KEY_REUSED:
    'This request key was already used for a different stock selection. Reload the order before trying again.',
  LINE_TO_EXISTING_INVENTORY_NEEDS_MYSQL:
    'Using existing stock is not enabled on this server yet. Ask engineering to enable it.',
};

export const describeLineToExistingInventoryRefusal = (code: string | undefined): string | undefined =>
  code ? REFUSALS[code] : undefined;
